import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard } from "lucide-react";
import PageMeta from "./PageMeta";
import MembershipApplication from "./MembershipApplication";

const reasons = ["Membership", "Boat Trip", "Competition Entry", "Other"];

export default function PaymentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnedFromCard = searchParams.get("paid") === "1";

  const [reason, setReason]       = useState("");
  const [details, setDetails]     = useState("");
  const [name, setName]           = useState("");
  const [email, setEmail]         = useState("");
  const [phone, setPhone]         = useState("");
  const [amount, setAmount]       = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [cardEnabled, setCardEnabled] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError]     = useState("");

  useEffect(() => {
    fetch("/.netlify/functions/sumup-checkout")
      .then((res) => res.json())
      .then((data) => setCardEnabled(Boolean(data.enabled)))
      .catch(() => setCardEnabled(false));
  }, []);

  const amountValue = parseFloat(amount);
  const amountValid = Number.isFinite(amountValue) && amountValue > 0;
  const isMembership = reason === "Membership";
  const paymentReady = reason && !isMembership && details.trim() && name.trim() && email.trim() && phone.trim() && amountValid;
  const amountLabel = amountValid ? `£${amountValue.toFixed(2)}` : "";

  // Start each reason fresh so amounts and details don't carry over between options
  const chooseReason = (r) => {
    if (r === reason) return;
    setDetails("");
    setAmount("");
    setReason(r);
  };

  const encode = (data) =>
    Object.keys(data).map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k])).join("&");

  const postClubPayment = (data) =>
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "club-payment", membershipType: "", reference: "", ...data }),
    }).catch(() => {});

  const submitPaymentToNetlify = (method, reference = "") =>
    postClubPayment({ reason, details, name, email, phone, amount: amountValue.toFixed(2), method, reference });

  // Membership applications also log a club payment flagged as Membership, so the treasurer is emailed
  const recordMembershipPayment = ({ membershipType, ...member }) =>
    postClubPayment({ reason: "Membership", membershipType, details: `${membershipType} Membership`, ...member });

  const handleCardPay = async () => {
    if (!paymentReady || cardLoading) return;
    setCardLoading(true);
    setCardError("");
    try {
      const res = await fetch("/.netlify/functions/sumup-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, details, name, email, amount: amountValue.toFixed(2) }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || "Could not start card payment.");
      await submitPaymentToNetlify("Card (SumUp)", data.reference);
      window.location.href = data.url;
    } catch (err) {
      setCardError(`${err.message} Please try again or pay by bank transfer.`);
      setCardLoading(false);
    }
  };

  const handleBacsSubmit = () => {
    submitPaymentToNetlify("BACS");
    setSubmitted("bacs");
  };

  const inputClass = "rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-cyan-300/40";

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Club Payments — Membership, Boat Trips & Competitions | Redruth Sea Angling Association"
        description="Pay for Redruth Sea Angling Association membership, boat trips, competition entries and other club events by card or bank transfer."
        keywords="RSAA payments, pay for boat trip, competition entry fee, Redruth Sea Angling Association"
        url="https://www.redruthseaanglingassociation.co.uk/payments"
      />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <button type="button" onClick={() => navigate(-1)}
            className="standalone-only group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-black/20 backdrop-blur transition hover:-translate-x-0.5 hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white">
            ← Back
          </button>
          <div>
            <h1 className="heading-glow text-2xl font-bold text-white sm:text-3xl">Club Payments</h1>
            <p className="mt-1 text-sm text-slate-400">Membership, boat trips, competitions and other club events</p>
          </div>
        </div>

        {returnedFromCard && (
          <div className="mb-6 rounded-2xl border border-cyan-300/30 bg-cyan-400/10 px-5 py-4 panel-lift">
            <p className="text-sm text-cyan-100">Thank you — you've returned from SumUp. If your card payment went through, there's nothing more to do. If you have any problems, please contact the club.</p>
          </div>
        )}

        <div className="rounded-[2rem] border border-cyan-300/20 bg-slate-950/70 p-7 panel-lift">
          <h3 className="text-xl font-semibold text-white">Make a Payment</h3>
          <p className="mt-2 text-sm text-slate-300">Please complete all details below, then proceed to payment.</p>

          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300/80">Reason for Payment</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {reasons.map((r) => (
              <button key={r} type="button" onClick={() => chooseReason(r)}
                className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${reason === r ? "bg-cyan-400 text-slate-950" : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}>
                {r}
              </button>
            ))}
          </div>

          {!isMembership && (
            <>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <input type="text" value={details} onChange={(e) => setDetails(e.target.value)}
                placeholder={reason === "Boat Trip" ? "Which trip? (date / boat)" : reason === "Competition Entry" ? "Which competition? (name / date)" : "What is the payment for?"}
                className={`sm:col-span-2 ${inputClass}`} />
            </div>

            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300/80">Member Details</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <input type="text" placeholder="Member's Full Name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
              <input type="tel" placeholder="Telephone Number" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
              <input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">£</span>
                <input type="number" inputMode="decimal" min="0.01" step="0.01" placeholder="Amount" value={amount}
                  onChange={(e) => setAmount(e.target.value)} className={`w-full pl-8 ${inputClass}`} />
              </div>
            </div>

            {!paymentReady && (
              <p className="mt-5 rounded-xl border border-amber-300/20 bg-amber-400/10 px-4 py-2.5 text-sm text-amber-300">Choose a reason and complete all fields to unlock payment.</p>
            )}

            {cardEnabled && (
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleCardPay}
                  disabled={!paymentReady || cardLoading}
                  className={`flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4 text-base font-semibold shadow-xl transition ${
                    paymentReady && !cardLoading
                      ? "bg-gradient-to-r from-slate-100 to-white text-slate-950 shadow-slate-400/20 hover:scale-[1.05] hover:shadow-slate-300/30 active:scale-[0.97]"
                      : "cursor-not-allowed bg-slate-700 text-slate-400 shadow-none"
                  }`}
                >
                  <CreditCard className="h-5 w-5" />
                  {cardLoading ? "Opening secure payment…" : `Pay ${amountLabel} via Card`}
                </button>
              </div>
            )}
            {cardError && (
              <p className="mt-3 rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-2.5 text-sm text-red-300">{cardError}</p>
            )}

            {paymentReady && (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 panel-lift">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Pay by Bank Transfer (BACS)</p>
                <p className="mt-1 text-xs text-slate-400">Please use your name and "{reason}" as the payment reference.</p>
                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <span className="text-slate-400">Account name</span>
                  <span className="font-medium text-white">Redruth Sea Angling Association</span>
                  <span className="text-slate-400">Sort code</span>
                  <span className="font-medium text-white">30-97-00</span>
                  <span className="text-slate-400">Account number</span>
                  <span className="font-medium text-white">00184090</span>
                  <span className="text-slate-400">Amount</span>
                  <span className="font-medium text-white">{amountLabel}</span>
                </div>
                <div className="mt-4 border-t border-white/10 pt-4">
                  {submitted === "bacs" ? (
                    <p className="text-sm font-medium text-cyan-300">✓ Your details have been submitted — please complete your bank transfer.</p>
                  ) : (
                    <button type="button" onClick={handleBacsSubmit}
                      className="rounded-xl bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300">
                      Submit my details for BACS payment
                    </button>
                  )}
                </div>
              </div>
            )}
            </>
          )}
        </div>

        {isMembership && (
          <div className="mt-6">
            <MembershipApplication adultLabel="Senior" onPayment={recordMembershipPayment} />
          </div>
        )}
      </div>
    </div>
  );
}
